import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page, Layout, Card, Button, Banner, Text,
  TextField, DropZone, InlineStack, BlockStack,
  ProgressBar, Scrollable, Badge, Modal,
  Tabs, Select, Divider, Spinner,
  Combobox, Listbox, Icon, EmptySearchResult,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';

const LIGHTSPEED_API = window.location.origin + '/api/lightspeed';

function LightspeedVendorReturns() {
  const navigate = useNavigate();

  // ── Setup ─────────────────────────────────────────────────────────────────
  const [vrTab, setVrTab] = useState(0); // 0=enter, 1=browse, 2=create
  const [vrNumber, setVrNumber] = useState('');
  const [file, setFile] = useState(null);

  // Session status — read-only view of /api/session, which is auto-uploaded
  // by the Puppeteer warmer (see github.com/elinenberg/Puppeteer).
  const [sessionStatus, setSessionStatus] = useState({ loading: true });
  const refreshSessionStatus = async () => {
    try {
      const res = await fetch(`${LIGHTSPEED_API}/session-status`);
      const data = await res.json();
      setSessionStatus({ loading: false, ...data });
    } catch (e) {
      setSessionStatus({ loading: false, configured: false, error: e.message });
    }
  };
  useEffect(() => { refreshSessionStatus(); }, []);

  // Browse VRs
  const [vrList, setVrList] = useState([]);
  const [vrListLoading, setVrListLoading] = useState(false);
  const [vrListError, setVrListError] = useState('');

  // Create VR
  const [vendors, setVendors] = useState([]);
  const [shops, setShops] = useState([]);
  const [createVendorId, setCreateVendorId] = useState('');
  const [createShopId, setCreateShopId] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [shopSearch, setShopSearch] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  // ── Run / streaming ───────────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [success, setSuccess] = useState(null);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, ok: 0, skip: 0 });
  const [phase, setPhase] = useState('idle');
  const [verifyItems, setVerifyItems] = useState([]);
  const [verifySummary, setVerifySummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const logEndRef = useRef(null);

  const appendLog = (line) => {
    setLogs((prev) => {
      const next = [...prev, line];
      setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      return next;
    });
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  });

  const lsGet = async (path) => {
    const res = await fetch(`${LIGHTSPEED_API}${path}`, {
      headers: authHeaders(),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
    return body;
  };

  const lsPost = async (path, fields) => {
    const fd = new FormData();
    fields.forEach(([k, v]) => fd.append(k, v));
    const res = await fetch(`${LIGHTSPEED_API}${path}`, {
      method: 'POST',
      headers: authHeaders(),
      body: fd,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
    return body;
  };

  // ── Load VRs ──────────────────────────────────────────────────────────────
  const loadVrList = async () => {
    setVrListLoading(true);
    setVrListError('');
    try {
      const data = await lsGet('/vrs');
      setVrList(data.vrs || []);
      if (!data.vrs?.length) setVrListError('No vendor returns found.');
    } catch (e) {
      setVrListError(e.message);
    } finally {
      setVrListLoading(false);
    }
  };

  // ── Load vendors + shops for Create VR ────────────────────────────────────
  const loadCreateData = async () => {
    setLookupLoading(true);
    setCreateError('');
    try {
      const [vData, sData] = await Promise.all([
        lsGet('/vendors'),
        lsGet('/shops'),
      ]);
      setVendors(vData.vendors || []);
      setShops(sData.shops || []);
    } catch (e) {
      setCreateError(`Failed to load vendors/shops: ${e.message}`);
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Create VR ─────────────────────────────────────────────────────────────
  const handleCreateVr = async () => {
    if (!createVendorId || !createShopId) return;
    setCreateLoading(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const data = await lsPost('/create-vr', [
        ['vendor_id', createVendorId],
        ['shop_id', createShopId],
      ]);
      const newId = data.vendorReturnID;
      if (newId) {
        setCreateSuccess(`Created VR #${newId}`);
        setVrNumber(String(newId));
        setVrTab(0); // switch to "Enter VR Number" tab showing the new ID
      } else {
        setCreateError(data.warning || 'VR may have been created — check Lightspeed and enter the number manually.');
      }
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  // ── File drop ─────────────────────────────────────────────────────────────
  const handleDrop = (_d, accepted) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      setDone(false); setSuccess(null); setLogs([]);
      setVerifyItems([]); setVerifySummary(null);
    }
  };

  // ── Run ───────────────────────────────────────────────────────────────────
  const handleRun = async () => {
    if (!file || !vrNumber.trim()) return;
    setRunning(true); setDone(false); setSuccess(null); setLogs([]);
    setProgress({ current: 0, total: 0, ok: 0, skip: 0 });
    setVerifyItems([]); setVerifySummary(null);
    setPhase('running'); setShowModal(true);

    const formData = new FormData();
    formData.append('vr_number', vrNumber.trim());
    formData.append('file', file);

    try {
      const response = await fetch(`${LIGHTSPEED_API}/run`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Request failed' }));
        appendLog(`ERROR: ${err.detail || response.statusText}`);
        setSuccess(false); setDone(true); setRunning(false); setPhase('done');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const lines = part.split('\n');
          let event = 'log', data = null;
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            if (line.startsWith('data: ')) {
              try { data = JSON.parse(line.slice(6)); } catch { data = { message: line.slice(6) }; }
            }
          }
          if (!data) continue;
          if (event === 'log' || event === 'status') appendLog(data.message || '');
          else if (event === 'progress') setProgress(p => ({ ...p, ...data }));
          else if (event === 'verify_start') setPhase('verifying');
          else if (event === 'verify_item') setVerifyItems(prev => [...prev, data]);
          else if (event === 'verify_summary') setVerifySummary(data);
          else if (event === 'done') {
            setSuccess(data.success); setDone(true); setPhase('done');
            appendLog(data.success ? '✓✓✓ COMPLETE ✓✓✓' : `FAILED: ${data.message}`);
          }
        }
      }
    } catch (err) {
      appendLog(`Network error: ${err.message}`);
      setSuccess(false); setDone(true); setPhase('done');
    } finally {
      setRunning(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const progressPct = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100) : 0;

  const vrSelectOptions = [
    { label: 'Select a vendor return…', value: '' },
    ...vrList.map(vr => {
      // AJAX listing uses vendor_return_id/vendor_name/shop_name
      const id = vr.vendor_return_id || vr.vendorReturnID;
      const vendor = vr.vendor_name || vr.Vendor?.name || 'Unknown Vendor';
      const shop = vr.shop_name ? ` · ${vr.shop_name}` : '';
      const status = vr.status ? ` [${vr.status}]` : '';
      const sent = vr.sent_date && vr.sent_date !== '0000-00-00' ? ` sent ${vr.sent_date}` : '';
      return {
        label: `#${id} — ${vendor}${shop}${sent}${status}`,
        value: String(id),
      };
    }),
  ];

  const vendorChoices = useMemo(
    () => vendors.map(v => ({
      label: v.name || String(v.id || v.vendorID),
      value: String(v.id || v.vendorID),
    })),
    [vendors],
  );
  const shopChoices = useMemo(
    () => shops.map(s => ({
      label: s.name || String(s.shopID),
      value: String(s.shopID),
    })),
    [shops],
  );
  const filteredVendorChoices = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!q) return vendorChoices;
    return vendorChoices.filter(o => o.label.toLowerCase().includes(q));
  }, [vendorChoices, vendorSearch]);
  const filteredShopChoices = useMemo(() => {
    const q = shopSearch.trim().toLowerCase();
    if (!q) return shopChoices;
    return shopChoices.filter(o => o.label.toLowerCase().includes(q));
  }, [shopChoices, shopSearch]);
  const selectedVendorLabel =
    vendorChoices.find(o => o.value === createVendorId)?.label || '';
  const selectedShopLabel =
    shopChoices.find(o => o.value === createShopId)?.label || '';

  const canRun = file && vrNumber.trim() && sessionStatus.configured && !running;

  const modalTitle =
    phase === 'running' ? `Processing VR #${vrNumber}…`
    : phase === 'verifying' ? `Verifying VR #${vrNumber}…`
    : done && success ? `VR #${vrNumber} — Complete`
    : done ? `VR #${vrNumber} — Failed`
    : `VR #${vrNumber}`;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Page
      title="Lightspeed Vendor Returns"
      subtitle="Add items from a CSV to a vendor return"
      primaryAction={{ content: 'Back to Dashboard', onAction: () => navigate('/') }}
    >
      <Layout>
        {/* ── Main card ──────────────────────────────────────────────────── */}
        <Layout.Section variant="twoThirds">
          <Card>
            <BlockStack gap="500">
              <Text variant="headingMd" as="h2">Setup</Text>

              {/* ── Vendor Return selection ── */}
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Vendor Return</Text>
                <Tabs
                  tabs={[
                    { id: 'enter', content: 'Enter VR Number' },
                    { id: 'browse', content: 'Browse Existing' },
                    { id: 'create', content: 'Create New VR' },
                  ]}
                  selected={vrTab}
                  onSelect={(i) => {
                    setVrTab(i);
                    if (i === 1 && !vrList.length) loadVrList();
                    if (i === 2 && !vendors.length) loadCreateData();
                  }}
                  fitted
                />

                <div style={{ paddingTop: '6px' }}>
                  {/* Tab 0: Enter manually */}
                  {vrTab === 0 && (
                    <TextField
                      label=""
                      value={vrNumber}
                      onChange={setVrNumber}
                      placeholder="e.g. 15572"
                      disabled={running}
                      autoComplete="off"
                      helpText="Enter the numeric ID from the Lightspeed vendor return URL"
                    />
                  )}

                  {/* Tab 1: Browse */}
                  {vrTab === 1 && (
                    <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="end">
                            <div style={{ flex: 1 }}>
                              <Select
                                label=""
                                options={vrSelectOptions}
                                onChange={setVrNumber}
                                value={vrNumber}
                                disabled={running || vrListLoading}
                              />
                            </div>
                            <Button
                              onClick={loadVrList}
                              loading={vrListLoading}
                              disabled={running}
                            >
                              {vrList.length ? 'Refresh' : 'Load VRs'}
                            </Button>
                          </InlineStack>
                          {vrListError && (
                            <Banner tone="critical">
                              <p>{vrListError}</p>
                            </Banner>
                          )}
                      <Text variant="bodySm" tone="subdued" as="p">
                        <a
                          href="https://us.merchantos.com/?name=vendor_return.listings.vendor_returns&form_name=listing&__sort=status&__sort_dir=ASC"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#005bd3' }}
                        >
                          Open Vendor Returns in Lightspeed ↗
                        </a>
                      </Text>
                    </BlockStack>
                  )}

                  {/* Tab 2: Create new VR */}
                  {vrTab === 2 && (
                    <BlockStack gap="300">
                          {createError && (
                            <Banner tone="critical" onDismiss={() => setCreateError('')}>
                              <p>{createError}</p>
                            </Banner>
                          )}
                          {createSuccess && (
                            <Banner tone="success">
                              <p>{createSuccess} — VR number filled in on the Enter tab.</p>
                            </Banner>
                          )}
                          {lookupLoading ? (
                            <InlineStack gap="200" blockAlign="center">
                              <Spinner size="small" />
                              <Text variant="bodySm" tone="subdued" as="span">Loading vendors and stores…</Text>
                            </InlineStack>
                          ) : vendors.length > 0 || shops.length > 0 ? (
                            <>
                              <Combobox
                                activator={
                                  <Combobox.TextField
                                    prefix={<Icon source={SearchIcon} />}
                                    label="Vendor"
                                    placeholder="Search vendors…"
                                    value={vendorSearch || selectedVendorLabel}
                                    onChange={(v) => {
                                      setVendorSearch(v);
                                      if (createVendorId && v !== selectedVendorLabel) {
                                        setCreateVendorId('');
                                      }
                                    }}
                                    onFocus={() => {
                                      if (vendorSearch === selectedVendorLabel) {
                                        setVendorSearch('');
                                      }
                                    }}
                                    autoComplete="off"
                                    disabled={createLoading}
                                  />
                                }
                              >
                                {filteredVendorChoices.length > 0 ? (
                                  <Listbox
                                    onSelect={(val) => {
                                      setCreateVendorId(val);
                                      const sel = vendorChoices.find(o => o.value === val);
                                      setVendorSearch(sel?.label || '');
                                    }}
                                  >
                                    {filteredVendorChoices.slice(0, 200).map(opt => (
                                      <Listbox.Option
                                        key={opt.value}
                                        value={opt.value}
                                        selected={createVendorId === opt.value}
                                        accessibilityLabel={opt.label}
                                      >
                                        {opt.label}
                                      </Listbox.Option>
                                    ))}
                                    {filteredVendorChoices.length > 200 && (
                                      <Listbox.Option value="__more__" disabled accessibilityLabel="More results">
                                        {`+${filteredVendorChoices.length - 200} more — keep typing to narrow…`}
                                      </Listbox.Option>
                                    )}
                                  </Listbox>
                                ) : (
                                  <EmptySearchResult
                                    title="No vendors match"
                                    description="Try a different search term."
                                    withIllustration={false}
                                  />
                                )}
                              </Combobox>
                              <Combobox
                                activator={
                                  <Combobox.TextField
                                    prefix={<Icon source={SearchIcon} />}
                                    label="Store / Location"
                                    placeholder="Search stores…"
                                    value={shopSearch || selectedShopLabel}
                                    onChange={(v) => {
                                      setShopSearch(v);
                                      if (createShopId && v !== selectedShopLabel) {
                                        setCreateShopId('');
                                      }
                                    }}
                                    onFocus={() => {
                                      if (shopSearch === selectedShopLabel) {
                                        setShopSearch('');
                                      }
                                    }}
                                    autoComplete="off"
                                    disabled={createLoading}
                                  />
                                }
                              >
                                {filteredShopChoices.length > 0 ? (
                                  <Listbox
                                    onSelect={(val) => {
                                      setCreateShopId(val);
                                      const sel = shopChoices.find(o => o.value === val);
                                      setShopSearch(sel?.label || '');
                                    }}
                                  >
                                    {filteredShopChoices.map(opt => (
                                      <Listbox.Option
                                        key={opt.value}
                                        value={opt.value}
                                        selected={createShopId === opt.value}
                                        accessibilityLabel={opt.label}
                                      >
                                        {opt.label}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox>
                                ) : (
                                  <EmptySearchResult
                                    title="No stores match"
                                    description="Try a different search term."
                                    withIllustration={false}
                                  />
                                )}
                              </Combobox>
                              <InlineStack gap="200">
                                <Button
                                  variant="primary"
                                  onClick={handleCreateVr}
                                  loading={createLoading}
                                  disabled={!createVendorId || !createShopId || createLoading}
                                >
                                  Create Vendor Return
                                </Button>
                                <Button onClick={loadCreateData} disabled={createLoading}>
                                  Reload
                                </Button>
                              </InlineStack>
                            </>
                          ) : (
                            <Button onClick={loadCreateData}>
                              Load Vendors &amp; Stores
                            </Button>
                          )}
                    </BlockStack>
                  )}
                </div>
              </BlockStack>

              <Divider />

              {/* ── CSV upload ── */}
              <BlockStack gap="100">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingSm" as="h3">Items CSV</Text>
                  <Text variant="bodySm" tone="subdued" as="span">
                    Columns: <strong>sku</strong>, <strong>quantity</strong>
                  </Text>
                </InlineStack>
                <DropZone onDrop={handleDrop} accept=".csv" allowMultiple={false} disabled={running}>
                  <div style={{ padding: '10px 14px' }}>
                    {file ? (
                      <InlineStack gap="300" blockAlign="center" wrap={false}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <BlockStack gap="0">
                          <Text variant="bodyMd" as="p">{file.name}</Text>
                          <Text variant="bodySm" tone="subdued" as="p">{(file.size / 1024).toFixed(1)} KB</Text>
                        </BlockStack>
                        {!running && (
                          <div style={{ marginLeft: 'auto' }}>
                            <Button size="slim" tone="critical" variant="plain"
                              onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                              Remove
                            </Button>
                          </div>
                        )}
                      </InlineStack>
                    ) : (
                      <InlineStack gap="200" blockAlign="center">
                        <span style={{ fontSize: '18px', opacity: 0.5 }}>📂</span>
                        <Text variant="bodySm" tone="subdued" as="span">
                          Drop CSV here or click to browse
                        </Text>
                      </InlineStack>
                    )}
                  </div>
                </DropZone>
              </BlockStack>

              {!sessionStatus.loading && !sessionStatus.configured && (
                <Banner tone="warning">
                  <p>
                    No Lightspeed session uploaded yet. The Puppeteer warmer on the
                    operator Mac uploads cookies every 10&nbsp;min — check that it's
                    running.
                  </p>
                </Banner>
              )}

              <Button
                variant="primary"
                size="large"
                onClick={handleRun}
                disabled={!canRun}
                loading={running}
              >
                {running ? 'Running…' : 'Run Vendor Return Import'}
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ── Session status sidebar ────────────────────────────────────── */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd" as="h2">Lightspeed Session</Text>
                <Button onClick={refreshSessionStatus} variant="plain" size="slim">
                  Refresh
                </Button>
              </InlineStack>
              {sessionStatus.loading ? (
                <Spinner size="small" />
              ) : sessionStatus.configured ? (
                <BlockStack gap="200">
                  <Badge tone="success">Auto-uploaded</Badge>
                  <Text variant="bodySm" as="p">
                    <strong>{sessionStatus.cookie_count}</strong> cookies, last validated{' '}
                    <strong>{sessionStatus.validated_at || sessionStatus.mtime || '—'}</strong>.
                  </Text>
                  {sessionStatus.uploaded_by && (
                    <Text variant="bodySm" tone="subdued" as="p">
                      Source: {sessionStatus.uploaded_by}
                    </Text>
                  )}
                  <Text variant="bodySm" tone="subdued" as="p">
                    Captured on the operator Mac and pushed by the Puppeteer
                    warmer. The current session stays in place until a new
                    upload passes validation, so a temporary capture failure
                    won't break this page.
                  </Text>
                </BlockStack>
              ) : (
                <BlockStack gap="200">
                  <Badge tone="critical">Not configured</Badge>
                  <Text variant="bodySm" as="p">
                    Waiting for the first Lightspeed cookie upload from the
                    Puppeteer warmer (
                    <a
                      href="https://github.com/elinenberg/Puppeteer"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      github.com/elinenberg/Puppeteer
                    </a>
                    ). Confirm the LaunchAgent on the operator Mac is running.
                  </Text>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* ── Progress Modal ────────────────────────────────────────────────── */}
      <Modal
        open={showModal}
        onClose={() => { if (!running) setShowModal(false); }}
        title={modalTitle}
        primaryAction={done ? { content: 'Close', onAction: () => setShowModal(false) } : undefined}
        large
      >
        <Modal.Section>
          <BlockStack gap="500">

            {/* Progress bar */}
            <BlockStack gap="150">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="bodyMd" as="span">
                  {phase === 'verifying'
                    ? `Verifying ${verifyItems.length} / ${progress.total} items…`
                    : phase === 'done' ? 'Finished'
                    : progress.total > 0 ? `Item ${progress.current} of ${progress.total}`
                    : 'Starting up…'}
                </Text>
                {phase === 'running' && progress.total > 0 && (
                  <Text variant="bodyMd" tone="subdued" as="span">{progressPct}%</Text>
                )}
              </InlineStack>
              <ProgressBar
                progress={phase === 'verifying' || phase === 'done' ? 100 : progressPct}
                size="medium"
                tone={done && !success ? 'critical' : done ? 'success' : 'highlight'}
              />
            </BlockStack>

            {/* Stats */}
            {progress.total > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                <StatCard value={progress.total} label="Total" bg="#f0f4ff" color="#1a1a2e" />
                <StatCard value={progress.ok} label="Added ✓" bg="#f0fdf4" color="#008060" />
                <StatCard value={progress.skip} label="Skipped ✗" bg="#fff7f0" color="#c05717" />
                <StatCard value={Math.max(0, progress.total - progress.current)} label="Remaining" bg="#f6f6f7" color="#6d7175" />
              </div>
            )}

            {/* Live log */}
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Live Log</Text>
              <Scrollable style={{ height: '200px', background: '#0f0f1a', borderRadius: '6px', padding: '10px 12px' }}>
                {logs.map((line, i) => (
                  <div key={i} style={{
                    fontFamily: 'monospace', fontSize: '11px', lineHeight: '1.5',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    color:
                      line.startsWith('!!') || line.includes('FAILED') || line.startsWith('   !') ? '#ff6b6b'
                      : line.startsWith('✓') || line.includes('COMPLETE') ? '#51cf66'
                      : line.startsWith('   ~') || line.includes('already') ? '#ffd43b'
                      : line.startsWith('   ?') ? '#ff9f43'
                      : line.startsWith('>>') ? '#74c0fc'
                      : '#d0d0d0',
                  }}>{line}</div>
                ))}
                <div ref={logEndRef} />
              </Scrollable>
            </BlockStack>

            {/* Verification table */}
            {verifyItems.length > 0 && (
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingSm" as="h3">Verification Results</Text>
                  {verifySummary && (
                    <InlineStack gap="150">
                      <Badge tone="success">{verifySummary.ok} correct</Badge>
                      {verifySummary.fixed > 0 && <Badge tone="success">{verifySummary.fixed} qty fixed</Badge>}
                      {verifySummary.wrong_qty > 0 && <Badge tone="caution">{verifySummary.wrong_qty} wrong qty</Badge>}
                      {verifySummary.missing > 0 && <Badge tone="critical">{verifySummary.missing} missing</Badge>}
                      <Badge tone="info">{verifySummary.total_in_vr} in VR</Badge>
                    </InlineStack>
                  )}
                </InlineStack>
                <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e1e3e5', borderRadius: '8px', fontSize: '12px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f6f6f7', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={thStyle}>SKU</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '80px' }}>Expected</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '80px' }}>Actual</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '90px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verifyItems.map((v, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{v.sku}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{v.expected}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: v.actual !== v.expected ? 700 : 400 }}>{v.actual}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {v.status === 'ok'
                              ? <span style={{ color: '#008060' }}>✓ OK</span>
                              : v.status === 'fixed'
                              ? <span style={{ color: '#008060' }}>✓ fixed</span>
                              : v.status === 'wrong_qty'
                              ? <span style={{ color: '#c05717' }}>⚠ qty off</span>
                              : <span style={{ color: '#d72c0d' }}>✗ missing</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </BlockStack>
            )}

            {/* Final verdict */}
            {done && verifySummary && (
              verifySummary.missing === 0 && verifySummary.wrong_qty === 0 ? (
                <Banner tone="success" title="All items verified successfully.">
                  <p>
                    {verifySummary.ok} items correct
                    {verifySummary.fixed > 0 && `, ${verifySummary.fixed} quantities auto-corrected`}.
                  </p>
                </Banner>
              ) : (
                <Banner tone="critical" title="Some items need attention.">
                  <p>
                    {verifySummary.missing > 0 && `${verifySummary.missing} SKU(s) not found in Lightspeed (may not exist in this shop). `}
                    {verifySummary.wrong_qty > 0 && `${verifySummary.wrong_qty} item(s) still have incorrect quantities after auto-fix attempt. `}
                    {verifySummary.fixed > 0 && `${verifySummary.fixed} quantities were auto-corrected. `}
                  </p>
                </Banner>
              )
            )}

          </BlockStack>
        </Modal.Section>
      </Modal>

      <div style={{ height: 48 }} />
    </Page>
  );
}

function StatCard({ value, label, bg, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 8px', background: bg, borderRadius: '8px' }}>
      <div style={{ fontSize: '24px', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#6d7175', marginTop: '2px' }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '7px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e1e3e5', whiteSpace: 'nowrap' };
const tdStyle = { padding: '6px 12px' };

export default LightspeedVendorReturns;
